import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import * as cadQuoteApi from '../../lib/api/cadQuote';
import { featuresFromAnalysis } from '../../lib/cadQuoteCalc';

const formatWon = (value) => (value == null ? '-' : `${Math.round(Number(value) || 0).toLocaleString('ko-KR')}원`);

/**
 * 2단계 아래 - 지금 분석 중인 형상과 비슷한 과거 견적 최대 5건(CadQuoteSimilarityCalculator,
 * 백엔드 /quotes/similar). 저장된 견적이 없으면(이제 막 쓰기 시작한 회사) 당연히 빈 목록 -
 * 안내 문구로 "저장하면 다음부터 찾아준다"는 걸 알려준다.
 *
 * 레이어로 나눈 뒤에는 나눈 결과(partCnt/layerCnt)를 기준으로 다시 찾는다 - analysis가
 * split 응답으로 바뀌거나 form.parts 수/layered가 바뀌면 다시 조회한다.
 */
const CadQuoteSimilarPanel = ({ analysis, form, onApplyQuote, onOpenDetail }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!analysis) return undefined;
    let cancelled = false;
    setLoading(true);
    setErrorMessage('');
    const features = featuresFromAnalysis(analysis, form);
    cadQuoteApi.findSimilarCadQuotes(features)
      .then((res) => {
        if (cancelled) return;
        setRows(res?.data?.list || []);
      })
      .catch(() => {
        if (cancelled) return;
        setErrorMessage('비슷한 과거 견적을 찾는 데 실패했습니다.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // form 전체가 아니라 layered/parts.length만 본다 - 부품 값 하나 고칠 때마다(매 타이핑)
    // 다시 조회하면 너무 자주 호출된다. 전체 형상이 바뀌는 시점(분석/레이어 분할)에만 다시 찾는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysis, form?.layered, form?.parts?.length]);

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle1" gutterBottom>비슷한 과거 견적</Typography>

      {loading && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">찾는 중...</Typography>
        </Stack>
      )}

      {!loading && errorMessage && <Alert severity="warning">{errorMessage}</Alert>}

      {!loading && !errorMessage && rows.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          아직 저장된 견적이 없습니다 — 견적을 저장하면 다음부터 비슷한 건을 찾아줍니다.
        </Typography>
      )}

      {!loading && rows.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="right">유사도</TableCell>
                <TableCell>품번</TableCell>
                <TableCell>재질</TableCell>
                <TableCell align="right">부품/레이어</TableCell>
                <TableCell align="right">견적 합계</TableCell>
                <TableCell align="right">실제 견적가</TableCell>
                <TableCell>날짜</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.qtUuid}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onOpenDetail(row.qtUuid)}
                >
                  <TableCell align="right">{Math.round(row.similarity)}%</TableCell>
                  <TableCell>{row.partNo || '-'}</TableCell>
                  <TableCell>{row.mainMtrlCd || '-'}</TableCell>
                  <TableCell align="right">{row.partCnt ?? '-'}/{row.layerCnt ?? '-'}</TableCell>
                  <TableCell align="right">{formatWon(row.totalAmt)}</TableCell>
                  <TableCell align="right">{row.finalAmt != null ? formatWon(row.finalAmt) : '-'}</TableCell>
                  <TableCell>{row.regDt}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation(); // 행 클릭(상세 열기)과 버튼 클릭이 겹치지 않게.
                        onApplyQuote(row.qtUuid);
                      }}
                    >
                      단가 가져오기
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
};

export default CadQuoteSimilarPanel;
